import { useCallback, useState, useEffect, useMemo } from "react";
import { Button, Modal as BootstrapModal } from "react-bootstrap";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";

import TextBox from "../controls/form/TextBox";
import RequestManager from "../../services/RequestManager";
import Product from "../../services/product";
import Status from "../../services/status";
import PubSub from "../../services/PubSub";
import Icon from "../controls/Icon";
import GenericDropdown from "../controls/GenericDropdown";
import Utils from "../../utils/utils";

const MigrateProducts = ({ show, onCancel }) => {
    const [translate] = useTranslation();
    const [hasNext, setHasNext] = useState(true);
    const [productList, setProductList] = useState([]);
    const [products, setProducts] = useState({});
    const [selectedProductVersions, setSelectedProductVersions] = useState([]);
    const [remoteServerUrl, setRemoteServerUrl] = useState("");
    const [authToken, setAuthToken] = useState("");
    const [settingsSetData, setSettingsSetData] = useState([]);
    const [showWarning, setShowWarning] = useState(false);
    const [selectedProductName, setSelectedProductName] = useState("");
    const [selectedProductVersionNumber, setSelectedProductVersionNumber] = useState("");

    useEffect(() => {
        if (show) {
            setSelectedProductName("");
            setSelectedProductVersionNumber("");
        }
    }, [show]);

    const getAllProductVersions = useCallback(async (sourceUrl, token) => {
        const response = await RequestManager.requestRemote({
            url: `${sourceUrl}/api/product/version/v1`,
            method: "GET",
            headers: {
                Authorization: token
            }
        });
        if (!response.error) {
            setProducts(response.data);
            setProductList(Object.keys(response.data).map((name) => {
                return {
                    _id: name,
                    name,
                };
            }));
        }
    }, [setProducts, setProductList]);

    const fetchSettingsSet = useCallback(async (id) => {
        const selectedVersion = selectedProductVersions.find(version => version._id === id);
        setSelectedProductVersionNumber(selectedVersion.productVersion);

        const response = await RequestManager.requestRemote({
            url: `${remoteServerUrl}/api/settingsSet/v1/${selectedVersion.settingsSet}?productVersion=${id}`,
            method: "GET",
            headers: {
                Authorization: authToken
            }
        });

        if (!response.error) {
            setSettingsSetData(response.data);
        } else {
            setHasNext(true);
        }
    }, [remoteServerUrl, authToken, setSettingsSetData, selectedProductVersions, setSelectedProductVersionNumber]);

    const areVersionsSame = useCallback(async (remoteUrl) => {
        const remoteVersion = await RequestManager.requestRemote({
            url: `${remoteUrl}/api/status/v1`,
            method: "GET"
        });

        const localVersion = await Status.getStatus();
        if (!remoteVersion.error && !localVersion.error) {
            return remoteVersion.data.version === localVersion.data.version;
        }
        return false;
    }, []);

    const moveToNextStep = useCallback(async ({ sourceUrl, email, password }) => {
        const success = await areVersionsSame(sourceUrl);
        if (success || showWarning) {
            const response = await RequestManager.requestRemote({
                url: `${sourceUrl}/api/auth/login/v1`,
                method: "POST",
                headers: {
                    grant_type: "authorization"
                },
                body: {
                    email,
                    password
                }
            });
            if (!response.error) {
                setHasNext(false);
                setRemoteServerUrl(sourceUrl);
                setAuthToken(response.data.token);
                await getAllProductVersions(sourceUrl, response.data.token);
            }
        } else {
            setShowWarning(true);
        }
    }, [showWarning, getAllProductVersions, setHasNext, setRemoteServerUrl, setAuthToken, areVersionsSame]);

    const selectProduct = useCallback((productName) => {
        setSelectedProductVersionNumber("");
        setSelectedProductName(productName);
        if (products[productName][0].productVersion) {
            setSelectedProductVersions([...products[productName]]);
        }
        else {
            setSelectedProductVersions([]);
        }
    }, [products, setSelectedProductVersions, setSelectedProductName, setSelectedProductVersionNumber]);

    const differentVersionWarning = useMemo(() => {
        if (!showWarning) {
            return null;
        }

        return (
            <div className='border border-2 border-info row mx-0 my-2'>
                <div className='bg-info col-1 py-2 d-flex'>
                    <Icon icon='InfoCircle' className='text-light fs-2 m-auto' />
                </div>
                <div className='col-11 py-2 mx-0'>
                    <p>{translate('OTM_MIGRATION_DIFFERENT_VERSION_WARNING')}</p>
                </div>
            </div>
        );
    }, [showWarning, translate]);

    const isValid = useCallback((destinationProductVersions) => {
        if (!destinationProductVersions || destinationProductVersions.length === 0) {
            return true;
        }
        const filteredVersions = destinationProductVersions.filter(version => version.productVersion === selectedProductVersionNumber);
        return filteredVersions.length === 0;
    }, [selectedProductVersionNumber]);

    const migrate = useCallback(async () => {
        const response = await Product.getAllProductVersions(Utils.getDomain());
        if (!response.error) {
            const productVersions = response.data[selectedProductName];

            if (isValid(productVersions)) {
                const remoteProduct = products[selectedProductName];
                await Product.migrateProductVersion(
                    {
                        ...settingsSetData,
                        name: selectedProductName,
                        version: selectedProductVersionNumber,
                        product: productVersions ? productVersions[0].product : null,
                        buildServer: remoteProduct[0].buildServer,
                        onPrem: remoteProduct[0].onPrem,
                        isForms: remoteProduct[0].isForms,
                    });

                PubSub.publish("refresh-product-list");
                onCancel();
            } else {
                PubSub.publish("show_info", "OTM_INVALID_PRODUCT_VERSION");
            }
        } else {
            PubSub.publish("show_info", "OTM_CANNOT_GET_PRODUCT_VERSIONS");
        }
    }, [settingsSetData, selectedProductName, products, isValid, selectedProductVersionNumber, onCancel]);

    const renderFirstForm = useMemo(() => {
        return (
            <Formik initialValues={{
                sourceUrl: remoteServerUrl || "",
                email: "",
                password: ""
            }} validationSchema={Yup.object({
                email: Yup.string()
                    .email(translate("OTM_INVALID_EMAIL"))
                    .required(" "),
                password: Yup.string()
                    .min(4, translate("OTM_CHARACTERS_OR_MORE", { minCharacters: 4 }))
                    .required(" "),
                sourceUrl: Yup.string(translate("OTM_INVALID_URL")).required(" ")
            })} onSubmit={moveToNextStep} enableReinitialize>
                <Form>
                    <BootstrapModal.Header className="h5 text-secondary">
                        {translate("OTM_MIGRATE_PRODUCTS")}
                    </BootstrapModal.Header>
                    <BootstrapModal.Body>
                        <TextBox label={translate("OTM_SOURCE_URL")} type="text" name="sourceUrl" />
                        <TextBox label={translate("OTM_EMAIL")} type="email" name="email" css="mt-3" />
                        <TextBox label={translate("OTM_PASSWORD")} type="password" name="password" css="mt-3" />
                        {differentVersionWarning}
                    </BootstrapModal.Body>
                    <BootstrapModal.Footer>
                        <Button type="submit" className="text-white btn-sm">{translate("OTM_NEXT")}</Button>
                        <Button onClick={onCancel} variant="secondary" className="text-white btn-sm">{translate("OTM_CANCEL")}</Button>
                    </BootstrapModal.Footer>
                </Form>
            </Formik>
        )
    }, [remoteServerUrl, differentVersionWarning, onCancel, moveToNextStep, translate]);

    const renderSecondForm = useMemo(() => {
        return (
            <>
                <BootstrapModal.Header className="h5 text-secondary">
                    {translate("OTM_MIGRATE_PRODUCTS")}
                </BootstrapModal.Header>
                <BootstrapModal.Body>
                    <GenericDropdown
                        options={productList}
                        valueKey="_id"
                        displayValueKey="name"
                        heading={translate("OTM_PRODUCT")}
                        onSelect={selectProduct}
                        grow={true}
                    />
                    <div className='mt-3'>
                        <GenericDropdown
                            options={selectedProductVersions}
                            valueKey='_id'
                            displayValueKey="productVersion"
                            heading={translate("OTM_SELECT_PRODUCT_VERSION")}
                            onSelect={fetchSettingsSet}
                            grow={true}
                        />
                    </div>
                </BootstrapModal.Body>
                <BootstrapModal.Footer>
                    <Button onClick={migrate} className="btn-sm">{translate("OTM_MIGRATE")}</Button>
                    <Button onClick={onCancel} variant="secondary" className="btn-sm">{translate("OTM_CANCEL")}</Button>
                </BootstrapModal.Footer>
            </>
        );
    }, [productList, selectedProductVersions, migrate, translate, onCancel, selectProduct, fetchSettingsSet]);

    return (
        <BootstrapModal show={show} backdrop="static">
            {hasNext && renderFirstForm}
            {!hasNext && renderSecondForm}
        </BootstrapModal>
    );
};

export default MigrateProducts;